import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import ImageUploader from '../ImageUploader';
import MockupControls from '../MockupControls';
import Icon from '../Icon';
import Spinner from '../Spinner';
import { fileToBase64 } from '../../services/geminiService';
import { FEMALE_CATEGORIES, MALE_CATEGORIES } from '../../constants/mockupData';

interface MockupToolProps {
    onGenerate: (cost: number, quality: 'standard' | 'hd', generator: (onStatusUpdate?: (msg: string) => void) => Promise<string>) => Promise<void>;
    isProcessing: boolean;
}

const MockupTool: React.FC<MockupToolProps> = ({ onGenerate, isProcessing }) => {
    const { t } = useLanguage();
    const uploaderRef = useRef<{ clearFiles: () => void }>(null);
    
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [incomingImageUrl, setIncomingImageUrl] = useState<string | null>(null);
    
    const [selectedMode, setSelectedMode] = useState<'female' | 'male' | 'studio'>('female');
    const [selectedCategory, setSelectedCategory] = useState(FEMALE_CATEGORIES[0].id);
    const [studioBg, setStudioBg] = useState('');

    useEffect(() => {
        const stored = localStorage.getItem('najeweller_direct_mockup');
        if (stored) {
            setIncomingImageUrl(stored);
            localStorage.removeItem('najeweller_direct_mockup');
        }
    }, []);

    useEffect(() => {
        const categories = selectedMode === 'male' ? MALE_CATEGORIES : FEMALE_CATEGORIES;
        if (!categories.find(c => c.id === selectedCategory)) {
            setSelectedCategory(categories[0].id);
        }
    }, [selectedMode]);

    const handleGenerate = async (quality: 'standard' | 'hd') => {
        if (uploadedFiles.length === 0 && !incomingImageUrl) return;
        
        // Cost Adjustment for 75% Profit Margin:
        // Standard (Flash): 40 Swarnas (Mockup has higher perceived value)
        // HD (Pro): 80 Swarnas (Significant computation/precision needed)
        const cost = quality === 'standard' ? 40 : 80;

        await onGenerate(cost, quality, async (onStatusUpdate) => {
            const { generateJewelryMockup } = await import('../../services/geminiService');
            
            let imagePart;
            if (incomingImageUrl) {
                const mimeType = incomingImageUrl.match(/data:([^;]+);/)?.[1] || 'image/png';
                const data = incomingImageUrl.split(',')[1];
                imagePart = { inlineData: { mimeType, data } };
            } else {
                const base64 = await fileToBase64(uploadedFiles[0]);
                imagePart = { inlineData: { mimeType: uploadedFiles[0].type, data: base64 } };
            }

            return await generateJewelryMockup(imagePart, selectedCategory, selectedMode, studioBg, quality, onStatusUpdate);
        });
    };

    const clearSource = () => {
        setIncomingImageUrl(null);
        setUploadedFiles([]);
        uploaderRef.current?.clearFiles();
    };

    const hasSource = uploadedFiles.length > 0 || !!incomingImageUrl;

    return (
        <div className="flex flex-col gap-6">
            <div className="space-y-3">
                <div className="flex justify-between items-center pl-1">
                    <label className="text-xs uppercase font-black tracking-widest text-slate-400">1. Product Source</label>
                    {hasSource && (
                        <button onClick={clearSource} className="text-xs font-bold text-red-500 hover:text-red-600 flex items-center gap-1">
                            <Icon name="close" className="w-3 h-3" /> Clear
                        </button>
                    )}
                </div>
                
                {incomingImageUrl ? (
                    <div className="relative group rounded-2xl overflow-hidden border border-apple-gold shadow-sm h-32 w-full bg-white flex items-center justify-center">
                        <img src={incomingImageUrl} className="max-h-full max-w-full object-contain p-2" alt="Incoming" />
                    </div>
                ) : (
                    <ImageUploader ref={uploaderRef} onFilesChange={setUploadedFiles} disabled={isProcessing} multiple={false} />
                )}
            </div>

            <div className="space-y-3">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">2. Configuration</label>
                <MockupControls 
                    selectedMode={selectedMode} setSelectedMode={setSelectedMode}
                    selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
                    studioBg={studioBg} setStudioBg={setStudioBg}
                    disabled={isProcessing}
                />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-black/5 dark:border-white/5">
                <button 
                    onClick={() => handleGenerate('standard')} 
                    disabled={isProcessing || !hasSource} 
                    className="flex flex-col items-center justify-center py-4 rounded-2xl font-bold transition-all active:scale-95 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 disabled:opacity-50 group"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                             <div className="flex items-center gap-2 mb-1">
                                <Icon name="sparkles" className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-white" />
                                <span className="text-sm">Standard</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-40">40 Swarnas</span>
                        </>
                    )}
                </button>

                <button 
                    onClick={() => handleGenerate('hd')} 
                    disabled={isProcessing || !hasSource} 
                    className="flex flex-col items-center justify-center py-4 rounded-2xl font-bold transition-all active:scale-95 bg-apple-gold text-black disabled:opacity-50 shadow-lg shadow-apple-gold/20 hover:shadow-apple-gold/40 hover:scale-[1.02]"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                            <div className="flex items-center gap-2 mb-1">
                                <Icon name="diamond" className="w-4 h-4 text-black" />
                                <span className="text-sm">HD Premium</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-60">80 Swarnas</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default MockupTool;